'use client';

import { ReactElement, createContext, useContext } from 'react';
import { useFeatureFlags } from '@provider-directory/contexts/FlagContext';
import { resolveDirectoryDisplayName } from '@provider-directory/functions/resolve-directory-display-name';
import { ClientUIConfig } from '@provider-directory/types';
import { HospitalAssociationsResource } from '@provider-directory/types/common.contracts';
import { ClientOrgContextKey } from '@provider-directory/types/organizations.contracts';
import type { RuntimeConfig } from '@provider-directory/types/runtime-config.contracts';
import OrgContextModel from './org-context-model';

const OrgContext = createContext<OrgContextModel | null>(null);

export const useOrgContext = (): OrgContextModel => {
  const context = useContext(OrgContext);
  if (!context) {
    throw new Error('useOrgContext must be used within an OrgContextProvider');
  }

  return context;
};

/**
 * Non-throwing variant of `useOrgContext`. Returns `null` when no
 * `OrgContextProvider` is mounted above. Use only from components where the
 * org context is an *optional* enrichment rather than a hard requirement —
 * e.g. `I18nProvider`, which can be mounted standalone from Storybook stories
 * that test isolated UI without the production provider chain.
 */
export const useOrgContextOptional = (): OrgContextModel | null => {
  return useContext(OrgContext);
};

/**
 * Resolves a provider's hospital associations to a subdirectory key.
 *
 * Config-driven: if every association code resolves to the same directory key,
 * that key is returned. Otherwise (mixed, empty, or unmapped codes) the fallback
 * key is used — typically the page-level parent directory.
 */
export const getOrgKeyBasedHospitalAssociations = (
  hospitalAssociations: HospitalAssociationsResource,
  hospitalAssociationMapping: Record<string, string> | undefined,
  fallbackKey: ClientOrgContextKey,
): ClientOrgContextKey => {
  if (hospitalAssociations.length === 0) {
    return fallbackKey;
  }

  const mapping = hospitalAssociationMapping ?? {};

  const resolvedKeys = hospitalAssociations.map((h) => {
    if (!(h.code in mapping)) {
      console.warn(
        `[OrgContext] Unmapped hospital association code: "${h.code}"`,
      );
    }
    return mapping[h.code] ?? fallbackKey;
  });
  const uniqueKeys = [...new Set(resolvedKeys)];

  if (uniqueKeys.length === 1) {
    return uniqueKeys[0] as ClientOrgContextKey;
  }

  // Mixed associations or unmapped codes → fall back to page-level directory
  return fallbackKey;
};

/**
 * Sibling slice for hospital-association resolution: `name` + `decisionTree`
 * only. Flag ON: active directory uses `runtimeConfig.name`; true siblings use
 * `directoryDisplayNames` (miss → active `runtimeConfig.name`).
 */
export interface SiblingDirectoryContext {
  key: ClientOrgContextKey;
  name: ClientUIConfig['name'];
  decisionTree: ClientUIConfig['decisionTree'];
}

export const useOrgContextBasedOnHospitalAssociations = (
  hospitalAssociations?: HospitalAssociationsResource | null,
): SiblingDirectoryContext => {
  const parentContext = useContext(OrgContext);
  const { featureFlags } = useFeatureFlags();

  if (!parentContext) {
    throw new Error(
      'useOrgContextBasedOnHospitalAssociations must be used within an OrgContextProvider',
    );
  }

  const orgConfigKey: ClientOrgContextKey = getOrgKeyBasedHospitalAssociations(
    hospitalAssociations ?? [],
    parentContext.runtimeConfig.hospitalAssociationMapping,
    parentContext.key,
  );

  const { decisionTree } = parentContext.allConfigs[orgConfigKey];
  let name: string;
  if (!featureFlags.isConfigRolloutGroupEnabled) {
    name = parentContext.allConfigs[orgConfigKey].name;
  } else if (orgConfigKey === parentContext.key) {
    // Self (empty/mixed associations, or same-site provider) — not a sibling
    // lookup. Standalone directories like acme have no directoryDisplayNames map.
    name = parentContext.runtimeConfig.name;
  } else {
    name = resolveDirectoryDisplayName(
      orgConfigKey,
      parentContext.runtimeConfig.directoryDisplayNames,
      parentContext.runtimeConfig.name,
    );
  }
  return { key: orgConfigKey, name, decisionTree };
};

export const OrgContextProvider = ({
  children,
  orgKey,
  runtimeConfig,
}: {
  children: React.ReactNode;
  orgKey: ClientOrgContextKey;
  runtimeConfig: RuntimeConfig;
}): ReactElement => {
  const { featureFlags } = useFeatureFlags();
  return (
    <OrgContext.Provider
      value={new OrgContextModel(orgKey, featureFlags, runtimeConfig)}
    >
      {children}
    </OrgContext.Provider>
  );
};
